import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setShowContextMenu } from "@/store/iframeSlice";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TempoElement } from "@/utils/tempoElement";
import {
  DuplicateChange,
  RemoveJsxChange,
  WrapDivChange,
} from "@/store/zustand/changeLedgerTypes";
import {
  CursorType,
  ModalType,
  setCursorType,
  setModal,
} from "@/store/globalSettingsSlice";
import {
  useCanvasStore,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { JsxElementType } from "@/utils/jsx/jsxElementTypes";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import {
  getComponentFileFromNavTreeNodes,
  getLookupKeyForComponent,
  getStoriesForComponent,
} from "@/utils/jsx/extractElementsUtils";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  useActiveCanvas,
  useActiveProject,
  useAutoUpdateRef,
  useCanvasId,
  isMacOS,
} from "@/utils/utils";
import { KEYBOARD_SHORTCUT_DISPLAY_HELPERS } from "@/hooks/keyboard-shortcuts";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useDeleteStoryboards } from "../../hooks/storyboards/useDeleteStoryboards";
import { useDuplicateStoryboard } from "../../hooks/storyboards/useDuplicateStoryboard";
import { toast } from "sonner";
import { ACTION_TYPES } from "@/utils/ai/utils";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { usePublishComponentModalStore } from "@/features/publishComponent/publishComponentModalStore";
import { Storyboard } from "@/utils/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import useLocalMode from "@/hooks/useLocalMode";
import axios from "axios";

import { useSession } from "@clerk/clerk-react";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { isChrome } from "@/utils/browserDetection";
import { GenerateStoriesModal } from "./GenerateStoriesModal";
import { useWrapStoryboards } from "@/hooks/storyboards/useWrapStoryboards";
import { useLoadCodebase } from "@/utils/devServerUtils";
import { v4 as uuidv4 } from "uuid";
import { updateCodebaseId } from "./Storyboard/StaticIframe/utils/utils";
import { useDetachInstance } from "@/hooks/useDetachInstance";
import { useCreateComponent } from "@/hooks/canvas/useCreateComponent";
import { useGroupElements } from "@/hooks/canvas/useGroupElements";
import { usePasteToReplace } from "./hooks/useCopyPaste";
import { useDesignPropertyShortcuts } from "./hooks/useDesignPropertyShortcuts";

interface Props {
  showMenu: boolean;
  left: number;
  top: number;
}

export enum DualContextMenuOptions {
  // Both storyboard & element controls
  Duplicate = "Duplicate",
  Delete = "Delete",
  OpenInVSCode = "OpenInVSCode",
  OpenInEditor = "OpenInEditor",
  AIEdit = "AIEdit",
  DetachInstance = "DetachInstance",
  PasteToReplace = "PasteToReplace",
  AutoLayoutToggle = "AutoLayoutToggle",
}

export enum ElementContextMenuOptions {
  // Element only controls
  WrapInDiv = "WrapInDiv",
  EditComponent = "EditComponent",
  CreateComponent = "CreateComponent",
  CreateStory = "CreateStory",
}

export enum StoryboardContextMenuOptions {
  // Storyboard only controls
  Publish = "Publish",
}

export enum CanvasContextMenuOptions {
  // Canvas only controls
  Paste = "Paste",
}

export enum ContextType {
  STORYBOARD = "STORYBOARD",
  ELEMENT = "ELEMENT",
  CANVAS = "CANVAS",
}

const isMac = isMacOS();
const CONTEXT_MENU_SHORTCUTS = {
  [CanvasContextMenuOptions.Paste]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["mod", "v"],
      isMac,
    ),
  [ElementContextMenuOptions.WrapInDiv]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["mod", "alt", "g"],
      isMac,
    ),
  [DualContextMenuOptions.Duplicate]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["mod", "d"],
      isMac,
    ),
  [DualContextMenuOptions.DetachInstance]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["mod", "alt", "b"],
      isMac,
    ),
  [DualContextMenuOptions.OpenInEditor]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["alt", "4"],
      isMac,
    ),
  [DualContextMenuOptions.AIEdit]: "A",
  [DualContextMenuOptions.PasteToReplace]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["mod", "shift", "r"],
      isMac,
    ),
  [DualContextMenuOptions.AutoLayoutToggle]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["shift", "a"],
      isMac,
    ),
  [ElementContextMenuOptions.CreateComponent]:
    KEYBOARD_SHORTCUT_DISPLAY_HELPERS.formatKeyboardShortcut(
      ["mod", "alt", "k"],
      isMac,
    ),
} as const;

// Canvas context menu items component
const CanvasContextMenuItems: React.FC<{
  onClickElemContextMenu: (option: CanvasContextMenuOptions) => void;
}> = ({ onClickElemContextMenu }) => (
  <DropdownMenuItem
    onClick={() => {
      onClickElemContextMenu(CanvasContextMenuOptions.Paste);
    }}
  >
    Paste
    <DropdownMenuShortcut>
      {CONTEXT_MENU_SHORTCUTS[CanvasContextMenuOptions.Paste]}
    </DropdownMenuShortcut>
  </DropdownMenuItem>
);

// Element or storyboard context menu items component
const ElementOrStoryboardContextMenuItems: React.FC<{
  selectedStoryboard: any;
  multiselectedStoryboards: any[] | undefined;
  multiselectedElements: TempoElement[];
  tempoElement: TempoElement;
  selectedTreeElement: any;
  sourceComponent: any;
  isLocalMode: boolean;
  publishComponentEnabled: boolean | undefined;
  selectionText: string;
  hasAutoLayout: boolean;
  onClickElemContextMenu: (option: any) => void;
}> = ({
  selectedStoryboard,
  multiselectedStoryboards,
  multiselectedElements,
  tempoElement,
  selectedTreeElement,
  sourceComponent,
  isLocalMode,
  publishComponentEnabled,
  selectionText,
  hasAutoLayout,
  onClickElemContextMenu,
}) => (
  <>
    {!multiselectedStoryboards?.length &&
      selectedStoryboard?.type === STORYBOARD_TYPES.COMPONENT &&
      !isLocalMode &&
      publishComponentEnabled && (
        <DropdownMenuItem
          onClick={() =>
            onClickElemContextMenu(StoryboardContextMenuOptions.Publish)
          }
        >
          <p>Publish to Marketplace</p>
        </DropdownMenuItem>
      )}
    <DropdownMenuItem
      onClick={() =>
        onClickElemContextMenu(ElementContextMenuOptions.WrapInDiv)
      }
    >
      <p>
        Wrap in <code className="text-xs">{"<div>"}</code>
      </p>
      <DropdownMenuShortcut>
        {CONTEXT_MENU_SHORTCUTS[ElementContextMenuOptions.WrapInDiv]}
      </DropdownMenuShortcut>
    </DropdownMenuItem>
    {/* Only allow duplicating for a single non-story storyboard, or for elements */}
    {(!selectedStoryboard ||
      (selectedStoryboard.type !== STORYBOARD_TYPES.STORY &&
        !multiselectedStoryboards?.length)) && (
      <DropdownMenuItem
        onClick={() => onClickElemContextMenu(DualContextMenuOptions.Duplicate)}
      >
        Duplicate
        <DropdownMenuShortcut>
          {CONTEXT_MENU_SHORTCUTS[DualContextMenuOptions.Duplicate]}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
    )}
    <DropdownMenuItem
      onClick={() => onClickElemContextMenu(DualContextMenuOptions.Delete)}
    >
      Delete{selectionText}
      <DropdownMenuShortcut>⌫</DropdownMenuShortcut>
    </DropdownMenuItem>
    {/* Only allow detach instance for a single non-application storyboard, or for a single element */}
    {((!selectedStoryboard && !multiselectedElements?.length) ||
      (selectedStoryboard &&
        selectedStoryboard.type !== STORYBOARD_TYPES.APPLICATION &&
        !multiselectedStoryboards?.length)) && (
      <DropdownMenuItem
        data-testid="context-menu-detach-instance"
        onClick={() =>
          onClickElemContextMenu(DualContextMenuOptions.DetachInstance)
        }
      >
        Detach Instance
        <DropdownMenuShortcut>
          {CONTEXT_MENU_SHORTCUTS[DualContextMenuOptions.DetachInstance]}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
    )}
    {isLocalMode && !multiselectedElements?.length && (
      <DropdownMenuItem
        onClick={() =>
          onClickElemContextMenu(DualContextMenuOptions.OpenInVSCode)
        }
      >
        Open in VSCode
      </DropdownMenuItem>
    )}
    {!isLocalMode && !multiselectedElements?.length && (
      <DropdownMenuItem
        onClick={() =>
          onClickElemContextMenu(DualContextMenuOptions.OpenInEditor)
        }
      >
        View Code
        <DropdownMenuShortcut>
          {CONTEXT_MENU_SHORTCUTS[DualContextMenuOptions.OpenInEditor]}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
    )}
    {!selectedStoryboard && tempoElement.isKnownElement() && (
      <DropdownMenuItem
        onClick={() =>
          onClickElemContextMenu(DualContextMenuOptions.PasteToReplace)
        }
      >
        Paste to Replace
        <DropdownMenuShortcut>
          {CONTEXT_MENU_SHORTCUTS[DualContextMenuOptions.PasteToReplace]}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
    )}
    {!selectedStoryboard && tempoElement.isKnownElement() && (
      <DropdownMenuItem
        onClick={() =>
          onClickElemContextMenu(DualContextMenuOptions.AutoLayoutToggle)
        }
      >
        {hasAutoLayout ? "Remove Auto Layout" : "Add Auto Layout"}
        <DropdownMenuShortcut>
          {CONTEXT_MENU_SHORTCUTS[DualContextMenuOptions.AutoLayoutToggle]}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
    )}
    {(selectedStoryboard || tempoElement.isKnownElement()) && (
      <DropdownMenuItem
        onClick={() => onClickElemContextMenu(DualContextMenuOptions.AIEdit)}
      >
        Edit With AI
        <DropdownMenuShortcut>
          {CONTEXT_MENU_SHORTCUTS[DualContextMenuOptions.AIEdit]}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
    )}
    {!selectedStoryboard && sourceComponent && (
      <DropdownMenuItem
        data-testid="context-menu-edit-component"
        onClick={() =>
          onClickElemContextMenu(ElementContextMenuOptions.EditComponent)
        }
      >
        Edit Component
      </DropdownMenuItem>
    )}
    {!selectedStoryboard &&
      selectedTreeElement?.type === JsxElementType.Element &&
      tempoElement.isKnownElement() &&
      !multiselectedElements?.length && (
        <DropdownMenuItem
          data-testid="context-menu-create-component"
          onClick={() => {
            onClickElemContextMenu(ElementContextMenuOptions.CreateComponent);
          }}
        >
          Create Component
          <DropdownMenuShortcut>
            {CONTEXT_MENU_SHORTCUTS[ElementContextMenuOptions.CreateComponent]}
          </DropdownMenuShortcut>
        </DropdownMenuItem>
      )}
    {
      // Not a storyboard, and a component instance or a component
      !selectedStoryboard &&
        selectedTreeElement &&
        ((selectedTreeElement.type === JsxElementType.ComponentInstance &&
          sourceComponent) ||
          selectedTreeElement.type === JsxElementType.Component) && (
          <DropdownMenuItem
            data-testid="context-menu-create-story"
            onClick={() => {
              onClickElemContextMenu(ElementContextMenuOptions.CreateStory);
            }}
          >
            Create Story
          </DropdownMenuItem>
        )
    }
  </>
);

const CanvasContextMenu: React.FC<Props> = React.memo(
  ({ left, top, showMenu }) => {
    const canvasId = useCanvasId();

    const { selectedElement, storyboardIdSelected } = useSelectedNavTreeNode();
    const { multiselectedElements, multiselectedStoryboardIds } =
      useMultiselectedNavTreeNode();

    const { isLocalMode, localFilePath } = useLocalMode();

    const [isCreateStoryModalOpen, setIsCreateStoryModalOpen] = useState(false);
    const [componentDataForStory, setComponentDataForStory] =
      useState<any>(null);

    const { session } = useSession();
    const publishComponentEnabled = true;
    const setActionType = useAiActionStore((store) => store.setActionType);

    const executeCommand = useDesignCommandManager(
      (store) => store.executeCommand,
    );

    // Extract the selected storyboards if the selected elements are storyboards
    const activeCanvas = useActiveCanvas();
    const { selectedStoryboard, multiselectedStoryboards } = useMemo<{
      selectedStoryboard?: Storyboard | null;
      multiselectedStoryboards?: Storyboard[];
    }>(() => {
      if (!activeCanvas?.storyboards || !storyboardIdSelected) {
        return {};
      }

      let selectedStoryboard: Storyboard | null = null;
      const multiselectedStoryboards: Storyboard[] = [];

      activeCanvas.storyboards.forEach((sb) => {
        if (storyboardIdSelected === sb.id) {
          selectedStoryboard = sb;
        }

        if (multiselectedStoryboardIds?.find((id) => id === sb.id)) {
          multiselectedStoryboards.push(sb);
        }
      });

      return {
        selectedStoryboard,
        multiselectedStoryboards,
      };
    }, [activeCanvas, storyboardIdSelected, multiselectedStoryboardIds]);

    const { deleteStoryboards, renderConfirmDeleteDialog } =
      useDeleteStoryboards();
    const duplicateStoryboard = useDuplicateStoryboard();

    const wrapStoryboards = useWrapStoryboards();
    const detachInstance = useDetachInstance();
    const createComponent = useCreateComponent();
    const groupElements = useGroupElements();
    const pasteToReplace = usePasteToReplace();
    const { applyAutoLayoutToggle } = useDesignPropertyShortcuts();

    const contextType = selectedStoryboard
      ? ContextType.STORYBOARD
      : selectedElement.isKnownElement()
        ? ContextType.ELEMENT
        : isChrome
          ? ContextType.CANVAS
          : null;

    const dispatch = useDispatch();

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup,
    );

    const setNewLayout = useLayoutStore((state) => state.setNewLayout);

    const setCanvasComponentKey = useCanvasStore(
      (store) => store.setCanvasComponentKey,
    );

    const fileToContentsFromApi = useCodebaseStore(
      (state) => state.fileToContents,
    );

    const project = useActiveProject();

    const selectedTreeElement = (treeElementLookup || {})[
      selectedElement.codebaseId
    ];

    const { selectedNavTreeNode } = useSelectedNavTreeNode();
    const { multiselectedNavTreeNodes } = useMultiselectedNavTreeNode();

    const hasAutoLayout = useMemo(() => {
      if (!selectedTreeElement || selectedStoryboard) return false;

      const elementsToCheck = multiselectedElements?.length
        ? multiselectedElements
        : selectedElement && !selectedElement.isEmpty()
          ? [selectedElement]
          : [];

      if (elementsToCheck.length === 0) return false;

      // Check if all elements have auto layout
      return elementsToCheck.every((element) => {
        const treeElement = treeElementLookup[element.codebaseId];
        if (!treeElement) return false;

        const elementClasses = treeElement.removableClasses || [];
        let hasFlex = false;
        let hasFlexPrefix = false;
        for (const cls of elementClasses) {
          if (cls === "flex") hasFlex = true;
          if (cls.startsWith("flex-")) hasFlexPrefix = true;
          if (hasFlex && hasFlexPrefix) return true;
        }
        return false;
      });
    }, [
      selectedTreeElement,
      selectedStoryboard,
      multiselectedElements,
      selectedElement,
      treeElementLookup,
    ]);

    // For component instances only, fetch the source component from the tree elements
    const { componentKeyToComponent } = useAssetComponents();
    const sourceComponent = useMemo(() => {
      if (!componentKeyToComponent) {
        return;
      }

      if (selectedTreeElement?.type !== JsxElementType.ComponentInstance) {
        return null;
      }

      const key = getLookupKeyForComponent(selectedTreeElement);
      return componentKeyToComponent[key];
    }, [selectedTreeElement, componentKeyToComponent]);

    // Check if there are existing stories for the component
    const hasExistingStories = useMemo(() => {
      const targetComponent = sourceComponent || selectedTreeElement;

      if (!targetComponent || !project || !treeElementLookup) {
        return false;
      }

      // Only check for stories if it's a component or component instance
      if (
        targetComponent.type !== JsxElementType.Component &&
        targetComponent.type !== JsxElementType.ComponentInstance
      ) {
        return false;
      }

      const stories = getStoriesForComponent(
        project,
        treeElementLookup,
        targetComponent,
      );
      return stories && stories.length > 0;
    }, [sourceComponent, selectedTreeElement, project, treeElementLookup]);

    const onClickElemContextMenu = async (
      contextMenuOption:
        | DualContextMenuOptions
        | ElementContextMenuOptions
        | StoryboardContextMenuOptions
        | CanvasContextMenuOptions,
    ) => {
      if (
        selectedElement.isEmpty() &&
        contextMenuOption !== CanvasContextMenuOptions.Paste
      ) {
        return;
      }

      switch (contextMenuOption) {
        case DualContextMenuOptions.Delete: {
          if (storyboardIdSelected) {
            deleteStoryboards(
              multiselectedStoryboardIds?.length
                ? multiselectedStoryboardIds.map((id) =>
                    TempoElement.forStoryboard(id),
                  )
                : [TempoElement.forStoryboard(storyboardIdSelected)],
            );
            break;
          }

          // Also just delete the storyboard
          if (selectedNavTreeNode && !selectedNavTreeNode?.parent) {
            deleteStoryboards([
              TempoElement.forStoryboard(
                selectedNavTreeNode.tempoElement.storyboardId,
              ),
            ]);
            break;
          }

          const removeChange = new RemoveJsxChange({
            codebaseIdsToRemove: multiselectedElements?.length
              ? multiselectedElements.map(
                  (tempoElement) => tempoElement.codebaseId,
                )
              : [selectedElement.codebaseId],
          });

          executeCommand(removeChange);

          // After instant update, deselect
          removeChange.clearSelectedElementsAfterInstantUpdate();

          // After undo, reselect
          removeChange.setSelectedElementsAfterUndoInstantUpdate(
            selectedElement.getKey(),
            multiselectedElements?.map((e) => e.getKey()),
          );

          break;
        }
        case DualContextMenuOptions.Duplicate: {
          if (storyboardIdSelected) {
            if (!multiselectedStoryboardIds?.length && selectedStoryboard) {
              duplicateStoryboard(selectedStoryboard);
            }

            break;
          }

          const dupChange = new DuplicateChange({
            codebaseIdsToDuplicate: multiselectedElements?.length
              ? multiselectedElements.map(
                  (tempoElement) => tempoElement.codebaseId,
                )
              : [selectedElement.codebaseId],
            codebaseIdHashingKey: uuidv4(),
          });

          const getNewTempoElement = (
            te: TempoElement,
            numDuplicatedBefore: number,
          ): TempoElement => {
            const uniquePathSegments = te.uniquePath.split("-");
            const newUniquePath = `${uniquePathSegments
              .slice(0, -1)
              .join("-")}-${
              Number(uniquePathSegments.slice(-1)) + 1 + numDuplicatedBefore
            }`;

            return new TempoElement(
              updateCodebaseId(
                te.codebaseId,
                dupChange.changeFields.codebaseIdHashingKey,
              ),
              selectedElement.storyboardId,
              newUniquePath,
            );
          };

          let newSelectedElementKey;

          if (!multiselectedElements?.length) {
            // Select the new element that was duplicated
            const newSelectedElement = getNewTempoElement(selectedElement, 0);
            newSelectedElementKey = newSelectedElement.getKey();
          }

          const newMultiselectedElementKeys: string[] = multiselectedElements
            ?.sort((a, b) => a.uniquePath.localeCompare(b.uniquePath))
            ?.map((te, i) => {
              const newElement = getNewTempoElement(te, i);

              if (te.isEqual(selectedElement)) {
                newSelectedElementKey = newElement.getKey();
              }

              return newElement.getKey();
            });

          dupChange.setSelectedElementsAfterInstantUpdate(
            newSelectedElementKey,
            newMultiselectedElementKeys,
          );

          executeCommand(dupChange);
          break;
        }
        case DualContextMenuOptions.DetachInstance: {
          await detachInstance();
          break;
        }
        case DualContextMenuOptions.OpenInEditor: {
          dispatch(
            setCursorType({
              cursorType: CursorType.Pointer,
            }),
          );
          // @ts-expect-error TS2345
          setNewLayout(LeftTab.FileSelector, null);
          break;
        }
        case DualContextMenuOptions.OpenInVSCode: {
          if (!localFilePath) {
            toast.error(
              'Local file path not found, please try re-running "npx run-tempo"',
            );
            break;
          }

          let localPath;

          let firstPathSegment = localFilePath;
          let secondPathSegment = selectedStoryboard
            ? selectedStoryboard?.fullFilePath?.split?.(canvasId)[1]
            : selectedTreeElement?.filename?.split?.(canvasId)?.[1];

          if (firstPathSegment && secondPathSegment) {
            if (firstPathSegment.endsWith("/")) {
              firstPathSegment = firstPathSegment.slice(0, -1);
            }

            if (secondPathSegment.startsWith("/")) {
              secondPathSegment = secondPathSegment.slice(1);
            }

            localPath = `${firstPathSegment}/${secondPathSegment}`;
          }

          const startLine = selectedStoryboard
            ? 0
            : selectedTreeElement?.startLine;

          if (localPath) {
            // Construct the URI
            const absolutePath = encodeURI(localPath);

            let vscodeUri = `vscode://file${absolutePath}`;

            if (startLine !== null && startLine !== undefined) {
              const encodedLineNumber = encodeURIComponent(startLine);
              vscodeUri += `:${encodedLineNumber}`;
            }

            window.open(vscodeUri, "_blank");
          }
          break;
        }
        case DualContextMenuOptions.AIEdit: {
          dispatch(
            setCursorType({
              cursorType: CursorType.Pointer,
            }),
          );
          setActionType(ACTION_TYPES.EDIT);
          dispatch(setModal(ModalType.AI));
          break;
        }
        case ElementContextMenuOptions.EditComponent: {
          if (sourceComponent) {
            setCanvasComponentKey(getLookupKeyForComponent(sourceComponent));
          }
          break;
        }
        case ElementContextMenuOptions.CreateComponent: {
          createComponent();
          break;
        }
        case ElementContextMenuOptions.CreateStory: {
          const nodes = multiselectedNavTreeNodes?.length
            ? multiselectedNavTreeNodes
            : selectedNavTreeNode
              ? [selectedNavTreeNode]
              : [];

          const { newFileContents: extractedElementFileContents } =
            (await getComponentFileFromNavTreeNodes(
              canvasId,
              nodes,
              undefined,
              undefined,
            )) ?? {};

          // For component instances, use source component info but include instance usage context
          let componentData;
          if (
            selectedTreeElement?.type === JsxElementType.ComponentInstance &&
            sourceComponent
          ) {
            componentData = {
              ...sourceComponent,
              // Add instance-specific usage context from static analysis
              instanceUsageContext: extractedElementFileContents,
              // Keep reference to the actual instance for additional context
              componentInstance: selectedTreeElement,
            };
          } else {
            // For direct components, use as-is
            componentData = selectedTreeElement;
          }

          setComponentDataForStory(componentData);
          setIsCreateStoryModalOpen(true);
          break;
        }
        case ElementContextMenuOptions.WrapInDiv: {
          // Check if we're wrapping storyboards or elements
          if (selectedStoryboard || multiselectedStoryboards?.length) {
            // Handle storyboard wrapping
            const storyboardsToWrap: Storyboard[] =
              multiselectedStoryboards?.length
                ? multiselectedStoryboards
                : [selectedStoryboard as Storyboard];

            if (!storyboardsToWrap?.length) {
              toast.error("No storyboards selected to wrap");
              break;
            }

            wrapStoryboards({
              sourceStoryboards: storyboardsToWrap,
            });
            break;
          } else {
            // Handle element wrapping
            groupElements();
            break;
          }
        }
        case StoryboardContextMenuOptions.Publish: {
          if (!selectedStoryboard || multiselectedStoryboards?.length) {
            break;
          }

          // Open the publish modal - all logic happens when user clicks "Publish" in modal
          usePublishComponentModalStore
            .getState()
            .openModal(selectedStoryboard);
          break;
        }
        case CanvasContextMenuOptions.Paste: {
          // Check browser compatibility
          if (!navigator.clipboard?.read) {
            toast.error("Paste not supported in this browser");
            break;
          }

          // Get actual clipboard data and create a synthetic event with it
          try {
            const clipboardItems = await navigator.clipboard.read();
            const dataTransfer = new DataTransfer();

            // Populate DataTransfer with actual clipboard content
            for (const item of clipboardItems) {
              for (const type of item.types) {
                const blob = await item.getType(type);
                if (type.startsWith("text/")) {
                  const text = await blob.text();
                  dataTransfer.setData(type, text);
                } else {
                  // For non-text types, create a File from the blob
                  const file = new File([blob], "clipboard-item", { type });
                  dataTransfer.items.add(file);
                }
              }
            }

            // Create synthetic event with populated clipboard data
            const syntheticEvent = new ClipboardEvent("paste", {
              bubbles: true,
              cancelable: true,
              clipboardData: dataTransfer,
            });

            // Set proper target so target.closest works
            Object.defineProperty(syntheticEvent, "target", {
              value: document.body,
              writable: false,
            });

            window.dispatchEvent(syntheticEvent);
          } catch (error) {
            console.error("Error accessing clipboard:", error);
            if (error.name === "NotAllowedError") {
              toast.error(
                "Clipboard access denied. Please allow clipboard permissions.",
              );
            } else {
              toast.error("Failed to access clipboard");
            }
          }
          break;
        }
        case DualContextMenuOptions.PasteToReplace: {
          if (!navigator.clipboard?.read) {
            toast.error("Paste not supported in this browser");
            break;
          }
          await pasteToReplace();
          break;
        }
        case DualContextMenuOptions.AutoLayoutToggle: {
          applyAutoLayoutToggle();
          break;
        }
        default:
          // Unknown context menu option
          break;
      }

      dismissRequestedContext();
    };

    const dismissRequestedContext = () => {
      dispatch(setShowContextMenu(false));
    };

    /**
     * For selected storyboards, indicates how many of each type are selected
     */
    const selectionText = useMemo(() => {
      if (!selectedStoryboard) {
        return "";
      }

      let numStoryboards = 0;
      let numStories = 0;

      const storyboards = multiselectedStoryboards?.length
        ? multiselectedStoryboards
        : [selectedStoryboard];

      // Count types
      storyboards.forEach((storyboard) => {
        storyboard.type === STORYBOARD_TYPES.STORY
          ? numStories++
          : numStoryboards++;
      });

      let text = " ";
      if (numStoryboards) {
        if (!numStories && numStoryboards === 1) {
          text += "Storyboard";
        } else {
          text += `${numStoryboards} Storyboard${
            numStoryboards > 1 ? "s" : ""
          }`;
        }
      }

      if (numStories) {
        if (!numStoryboards && numStories === 1) {
          text += "Story";
        } else {
          if (text) {
            text += ", ";
          }

          text += `${numStories} Stor${numStories > 1 ? "ies" : "y"}`;
        }
      }

      return text;
    }, [multiselectedStoryboards, selectedStoryboard]);

    // TODO: Scrolling when context menu doesn't dismiss as it did before
    return (
      <>
        {showMenu && contextType && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 9999, // Make sure this is less than your menu's zIndex
            }}
            className="pointer-events-auto"
            onClick={dismissRequestedContext}
            onContextMenu={(e) => {
              e.preventDefault();
              dismissRequestedContext();
            }}
            onWheel={dismissRequestedContext}
            onScroll={dismissRequestedContext}
            onTouchStart={dismissRequestedContext}
          />
        )}
        <DropdownMenu open={showMenu}>
          <DropdownMenuTrigger
            style={{ position: "fixed", top: top, left: left }}
          />
          <DropdownMenuContent
            side="right"
            style={{ zIndex: 10000 }}
            className="w-56"
            onInteractOutside={dismissRequestedContext}
            onFocusOutside={dismissRequestedContext}
          >
            <DropdownMenuLabel>
              <span>
                {contextType === ContextType.STORYBOARD
                  ? "Modify Storyboard"
                  : contextType === ContextType.ELEMENT
                    ? "Modify Element"
                    : contextType === ContextType.CANVAS
                      ? "Modify Canvas"
                      : "Modify Element"}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {contextType === ContextType.CANVAS ? (
                <CanvasContextMenuItems
                  onClickElemContextMenu={onClickElemContextMenu}
                />
              ) : contextType ? (
                <ElementOrStoryboardContextMenuItems
                  selectedStoryboard={selectedStoryboard}
                  multiselectedStoryboards={multiselectedStoryboards}
                  multiselectedElements={multiselectedElements}
                  tempoElement={selectedElement}
                  selectedTreeElement={selectedTreeElement}
                  sourceComponent={sourceComponent}
                  isLocalMode={isLocalMode}
                  publishComponentEnabled={publishComponentEnabled}
                  selectionText={selectionText}
                  hasAutoLayout={hasAutoLayout}
                  onClickElemContextMenu={onClickElemContextMenu}
                />
              ) : null}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {renderConfirmDeleteDialog()}

        <GenerateStoriesModal
          isOpen={isCreateStoryModalOpen}
          onClose={() => setIsCreateStoryModalOpen(false)}
          componentName={componentDataForStory?.componentName}
          componentPath={componentDataForStory?.filename}
          hasExistingStories={hasExistingStories}
          instanceUsageContext={componentDataForStory?.instanceUsageContext}
          componentInstance={componentDataForStory?.componentInstance}
        />
      </>
    );
  },
);

export default CanvasContextMenu;
